/**
 * Site usage — three reports under one time window
 * (docs/FeatureDesigns/acquisition/site-usage-target.md):
 *
 *   Science site   zitmus.com and zitmus.buzzbrain.ai — who read, what they
 *                  read. The science site is static HTML on a CDN, so none of
 *                  it exists in request logs; it is assembled from a beacon.
 *   BuzzBrain app  practice at buzzbrain.ai (AppUsageReport.tsx).
 *   Acquisition    published links followed, wherever they lead, and new
 *                  accounts by first touch; and the segment definitions its
 *                  funnels are asked about. Neither belongs to one property.
 *
 * Three reports rather than one with a property filter: the two properties'
 * funnels are different shapes, and merging them loses the detail of one.
 *
 * Inside the science site, one question per tab, because they are answered by
 * different shapes:
 *
 *   Overview      the totals and the trend of the filtered sessions
 *   Tables        which page, which simulation, which referrer — users,
 *                 sessions and events side by side, because one simulation
 *                 opened four times by one person is a different fact from
 *                 one opened once by four
 *   What sessions did   how far a session got, in sessions
 *   Flow          the routes readers take, page to page (SiteFlowChart.tsx)
 *   All landings  the page each session began on, and how far the new people
 *                 each page brought got (SiteLandings.tsx)
 *   Paths         the same sessions as a hierarchy, where a whole section can
 *                 be weighed against another
 *
 * The window is picked to the hour, with the volume it holds across both sites
 * beside it (VolumeLine) — before any report narrows it. A report's filter bar
 * sits above its tabs, so every tab describes the same sessions.
 */
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { TabStrip } from '@shared-ui/components';
import { WindowPicker } from './WindowPicker';
import { DEFAULT_HOURS, hoursAgo } from './window';
import { VolumeLine } from './VolumeLine';
import { AcquisitionReport } from './acquisition/AcquisitionReport';
import { ScienceSiteReport } from './science/ScienceSiteReport';
import AppUsageReport from './app/AppUsageReport';
import './SiteUsage.css';

/**
 * The three reports, one per thing that brings people in or keeps them.
 * Separate rather than one report with a property filter, because their
 * funnels are different shapes: a merged report either drops the app's detail
 * or fills the science site with steps it cannot have
 * (docs/FeatureDesigns/acquisition/site-usage-target.md §1).
 */
type View = 'science' | 'app' | 'acquisition';
// Acquisition first: where people came from is the question the page is
// opened for; the two property reports are what they then did.
const VIEWS = [
  { key: 'acquisition' as const, label: 'Acquisition' },
  { key: 'science' as const, label: 'Science site' },
  { key: 'app' as const, label: 'BuzzBrain app' },
];
const isView = (v: string | null): v is View => VIEWS.some(x => x.key === v);

/**
 * Site usage — the page. The window is shared by all three reports; each
 * report has its own filter bar, because what can be filtered depends on what
 * that property records. The filter values live here, so a person-level choice
 * (first source, campaign, new or returning) survives a switch between the two
 * property reports. The report shown is in the URL (`?view=`), so a link opens
 * on it.
 */
export default function SiteAnalytics() {
  // A week by default: the question this page is opened with is "what happened
  // lately", and a week loads fast; 30d and 90d are one click away.
  const [from, setFrom] = useState(() => hoursAgo(DEFAULT_HOURS));
  const [to, setTo] = useState(() => new Date());
  const [scopeValues, setScopeValues] = useState<Record<string, string>>({});
  const [params, setParams] = useSearchParams();
  const view: View = isView(params.get('view')) ? params.get('view') as View : 'acquisition';
  const setView = (v: View) => setParams(p => { p.set('view', v); return p; }, { replace: true });

  return (
    <div className="sa">
      <header className="sa-head">
        <h1>Site usage</h1>
        <div className="sa-window">
          <div className="sa-timerow">
            <WindowPicker from={from} to={to} setFrom={setFrom} setTo={setTo} />
          </div>
          {/* Under the window it describes: change the window, and this says how
              much the new one holds. */}
          <VolumeLine from={from} to={to} />
        </div>
      </header>

      <TabStrip tabs={VIEWS} active={view} onChange={setView} ariaLabel="Which report" className="sa-views" />

      {view === 'science' && (
        <ScienceSiteReport from={from} to={to} scopeValues={scopeValues} setScopeValues={setScopeValues} />
      )}
      {view === 'app' && (
        <AppUsageReport from={from} to={to} filters={scopeValues} setFilters={setScopeValues} />
      )}
      {view === 'acquisition' && <AcquisitionReport from={from} to={to} />}
    </div>
  );
}
